// ─── Page meta: single source of truth ────────────────────────────────────────
//
// Pure, framework-agnostic builders for the per-page <head> metadata (title,
// description, canonical URL, og:*/twitter:* tags and JSON-LD) of the two public
// route families: /events/:id and /churches/:slug.
//
// Both the client (react-helmet-async in EventDetailPage / ChurchProfilePage) and
// the server-side render route (packages/api) consume these builders, so the tags
// a JS-less crawler sees can never drift from what the hydrated SPA renders.
//
// No React, no Express, no DOM — keep this importable from any package.

import { getEventType } from './event-types'
import type { Church, FellowshipEvent } from './types/index'

export const SITE_NAME = 'Fellowship'
export const SITE_BASE_URL = 'https://fellowship-app.com'

// A single <meta> tag. Exactly one of `name` / `property` is set, matching the
// HTML attribute the tag uses (twitter:* + description use name, og:* use property).
export interface MetaTag {
  name?: string
  property?: string
  content: string
}

// Everything a consumer needs to render a page's <head>. `metaTags` is the flat,
// render-ready list of og:*/twitter:*/description tags; `jsonLd` is the structured
// data object (the caller stringifies it into a <script type="application/ld+json">).
export interface PageMeta {
  title: string
  description: string
  canonicalUrl: string
  image?: string
  ogType: string
  twitterCard: 'summary' | 'summary_large_image'
  metaTags: MetaTag[]
  jsonLd: Record<string, unknown>
}

// ─── Default event imagery ─────────────────────────────────────────────────────
//
// First-choice Unsplash image per event type, used when an event has no custom
// imageUrl. Kept here (not just in the web EventImagePicker) so server-rendered
// og:image matches the client. The web picker's getDefaultImageForType delegates
// to this so the "default = first curated image" choice lives in one place.
const DEFAULT_EVENT_IMAGE_ID: Record<string, string> = {
  potluck: 'photo-1529156069898-49953e39b3ac',
  worship: 'photo-1507692049790-de58290a4334',
  study: 'photo-1481627834876-b7833e8f5570',
  social: 'photo-1528605248644-14dd04022da1',
  outdoors: 'photo-1551632811-561732d1e306',
  kids: 'photo-1503454537195-1dcabb73ffb9',
  service: 'photo-1559027615-cd4628902d4a',
}

// Unsplash URL for an event type's default image at the requested `WxH` size.
// Unknown types (e.g. "groups") fall back to the potluck image, matching the web
// picker's behaviour.
export function getDefaultEventImageUrl(eventType: string, size = '600x400'): string {
  const id = DEFAULT_EVENT_IMAGE_ID[eventType] ?? DEFAULT_EVENT_IMAGE_ID.potluck
  const [w, h] = size.split('x')
  return `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&h=${h}&q=75`
}

// Truncate a description to a meta-friendly length without cutting mid-nothing.
function clampDescription(text: string): string {
  return text.length > 160 ? text.slice(0, 160) : text
}

// og:*/twitter:* tag set shared by both builders. `image` is optional: churches
// without a logo/cover fall back to a plain summary card with no image tag.
function buildSocialTags(meta: {
  title: string
  description: string
  canonicalUrl: string
  ogType: string
  twitterCard: 'summary' | 'summary_large_image'
  image?: string
}): MetaTag[] {
  const tags: MetaTag[] = [
    { name: 'description', content: meta.description },
    { property: 'og:title', content: meta.title },
    { property: 'og:description', content: meta.description },
    { property: 'og:type', content: meta.ogType },
    { property: 'og:url', content: meta.canonicalUrl },
  ]
  if (meta.image) tags.push({ property: 'og:image', content: meta.image })
  tags.push(
    { name: 'twitter:card', content: meta.twitterCard },
    { name: 'twitter:title', content: meta.title },
    { name: 'twitter:description', content: meta.description },
  )
  if (meta.image) tags.push({ name: 'twitter:image', content: meta.image })
  return tags
}

// ─── Events ────────────────────────────────────────────────────────────────────

// Minimal hosting-church shape the event builder needs — a subset of Church so
// callers can pass either a full Church or a lightweight projection.
export interface EventMetaHostingChurch {
  name: string
  slug: string
}

export interface BuildEventMetaOptions {
  // The church hosting a church-hosted event (drives the description fallback and
  // JSON-LD organizer). Null/undefined for person-hosted events.
  hostingChurch?: EventMetaHostingChurch | null
  // Next upcoming occurrence, used for the description fallback date and JSON-LD
  // startDate/endDate. Null/undefined when the event has no upcoming occurrence.
  nextOccurrenceStartsAt?: string | null
  nextOccurrenceEndsAt?: string | null
}

export function buildEventMeta(
  event: Pick<
    FellowshipEvent,
    'id' | 'eventType' | 'title' | 'description' | 'imageUrl' | 'addressText'
  >,
  options: BuildEventMetaOptions = {},
): PageMeta {
  const { hostingChurch, nextOccurrenceStartsAt, nextOccurrenceEndsAt } = options

  const canonicalUrl = `${SITE_BASE_URL}/events/${event.id}`
  const title = `${event.title} · ${SITE_NAME}`

  const typeLabel = getEventType(event.eventType ?? 'potluck').name
  const dateSuffix = nextOccurrenceStartsAt
    ? ` on ${new Date(nextOccurrenceStartsAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      })}`
    : ''
  const description = event.description
    ? clampDescription(event.description)
    : `${typeLabel} hosted by ${hostingChurch?.name ?? 'a local community'}${dateSuffix}.`

  const image = event.imageUrl ?? getDefaultEventImageUrl(event.eventType ?? 'potluck', '800x480')

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: event.description ?? description,
    url: canonicalUrl,
    image,
    location: {
      '@type': 'Place',
      address: event.addressText ?? '',
    },
  }
  if (nextOccurrenceStartsAt) {
    jsonLd.startDate = nextOccurrenceStartsAt
    jsonLd.endDate = nextOccurrenceEndsAt ?? nextOccurrenceStartsAt
    jsonLd.eventStatus = 'https://schema.org/EventScheduled'
    jsonLd.eventAttendanceMode = 'https://schema.org/OfflineEventAttendanceMode'
  }
  if (hostingChurch) {
    jsonLd.organizer = {
      '@type': 'Organization',
      name: hostingChurch.name,
      url: `${SITE_BASE_URL}/churches/${hostingChurch.slug}`,
    }
  }

  return {
    title,
    description,
    canonicalUrl,
    image,
    ogType: 'event',
    twitterCard: 'summary_large_image',
    metaTags: buildSocialTags({
      title,
      description,
      canonicalUrl,
      ogType: 'event',
      twitterCard: 'summary_large_image',
      image,
    }),
    jsonLd,
  }
}

// ─── Churches ──────────────────────────────────────────────────────────────────

export function buildChurchMeta(
  church: Pick<
    Church,
    | 'name'
    | 'slug'
    | 'description'
    | 'city'
    | 'state'
    | 'logoUrl'
    | 'coverImageUrl'
    | 'websiteUrl'
    | 'phone'
    | 'email'
    | 'addressLine1'
    | 'zip'
  >,
): PageMeta {
  const canonicalUrl = `${SITE_BASE_URL}/churches/${church.slug}`
  const title = `${church.name} · ${SITE_NAME}`

  const location = church.city ? ` in ${church.city}${church.state ? `, ${church.state}` : ''}` : ''
  const description = church.description
    ? clampDescription(church.description)
    : `${church.name}${location} — fellowship events on ${SITE_NAME}.`

  const image = church.coverImageUrl ?? church.logoUrl ?? undefined
  const twitterCard: 'summary' | 'summary_large_image' = image ? 'summary_large_image' : 'summary'

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Church',
    name: church.name,
    url: canonicalUrl,
    address: {
      '@type': 'PostalAddress',
      ...(church.addressLine1 && { streetAddress: church.addressLine1 }),
      ...(church.city && { addressLocality: church.city }),
      ...(church.state && { addressRegion: church.state }),
      ...(church.zip && { postalCode: church.zip }),
    },
  }
  if (church.description) jsonLd.description = church.description
  if (image) jsonLd.image = image
  if (church.logoUrl) jsonLd.logo = church.logoUrl
  if (church.websiteUrl) jsonLd.sameAs = church.websiteUrl
  if (church.phone) jsonLd.telephone = church.phone
  if (church.email) jsonLd.email = church.email

  return {
    title,
    description,
    canonicalUrl,
    image,
    ogType: 'website',
    twitterCard,
    metaTags: buildSocialTags({
      title,
      description,
      canonicalUrl,
      ogType: 'website',
      twitterCard,
      image,
    }),
    jsonLd,
  }
}
